<template>
  <div class="exam-grading">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2>阅卷管理</h2>
      <div class="header-actions">
        <el-checkbox v-model="showArchived" @change="loadUngradedExams">
          显示已归档
        </el-checkbox>
        <el-button
          type="warning"
          icon="el-icon-folder"
          @click="batchArchive"
          :disabled="selectedExams.length === 0"
        >
          批量归档 ({{ selectedExams.length }})
        </el-button>
        <el-button type="primary" icon="el-icon-refresh" @click="loadUngradedExams">刷新列表</el-button>
      </div>
    </div>

    <!-- 已上传试卷的考试列表 -->
    <div class="exam-list" v-loading="loading">
      <div v-if="exams.length === 0" class="empty-state">
        <i class="el-icon-document"></i>
        <p>暂无已上传试卷的考试</p>
        <p class="empty-tip">请先在"上传试卷"页面为考试上传试卷</p>
      </div>

      <el-table
        v-else
        :data="exams"
        border
        stripe
        style="width: 100%"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55" align="center"></el-table-column>
        <el-table-column prop="examName" label="考试名称" min-width="150">
          <template slot-scope="scope">
            <span class="exam-name" @click="openExamGrading(scope.row)">{{ scope.row.examName }}</span>
          </template>
        </el-table-column>

        <el-table-column prop="examDescription" label="考试描述" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.examDescription || '暂无描述' }}
          </template>
        </el-table-column>

        <el-table-column prop="examType" label="考试类型" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.examType === 1 ? 'primary' : 'success'" size="small">
              {{ scope.row.examType === 1 ? '手动组卷' : '随机组卷' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="totalScore" label="总分" width="80" align="center">
          <template slot-scope="scope">
            {{ scope.row.totalScore }} 分
          </template>
        </el-table-column>

        <el-table-column prop="examDuration" label="时长" width="80" align="center">
          <template slot-scope="scope">
            {{ scope.row.examDuration }} 分钟
          </template>
        </el-table-column>

        <el-table-column prop="paperCount" label="试卷套数" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.paperCount }} 套
          </template>
        </el-table-column>

        <el-table-column prop="status" label="状态" width="120" align="center">
          <template slot-scope="scope">
            <el-tag :type="getStatusTagType(scope.row.status)" size="small">
              {{ getStatusText(scope.row.status) }}
            </el-tag>
            <el-tag
              v-if="scope.row.status === 5"
              type="info"
              size="mini"
              style="margin-left: 5px;"
            >
              已归档
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="startTime" label="开始时间" width="150" align="center">
          <template slot-scope="scope">
            {{ formatTime(scope.row.startTime) }}
          </template>
        </el-table-column>

        <el-table-column prop="endTime" label="结束时间" width="150" align="center">
          <template slot-scope="scope">
            {{ formatTime(scope.row.endTime) }}
          </template>
        </el-table-column>

        <el-table-column label="操作" width="250" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button
              type="primary"
              size="mini"
              @click="startGrading(scope.row)"
              :disabled="scope.row.status === 5"
            >
              <i class="el-icon-edit"></i> 开始阅卷
            </el-button>
            <el-button
              v-if="scope.row.status !== 5"
              type="warning"
              size="mini"
              @click="archiveExam(scope.row)"
            >
              <i class="el-icon-folder"></i> 归档
            </el-button>
            <el-button
              v-if="scope.row.status === 5"
              type="success"
              size="mini"
              @click="unarchiveExam(scope.row)"
            >
              <i class="el-icon-folder-opened"></i> 取消归档
            </el-button>
            <el-button
              v-if="scope.row.status === 5"
              type="info"
              size="mini"
              @click="editArchiveSettings(scope.row)"
            >
              <i class="el-icon-setting"></i> 设置
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 学生列表对话框 -->
    <el-dialog
      title="选择学生进行批阅"
      :visible.sync="studentListDialogVisible"
      width="80%"
      :before-close="closeStudentListDialog"
    >
      <div class="student-list-container">
        <div class="exam-info">
          <h3>{{ currentExam.examName }}</h3>
          <p>{{ currentExam.examDescription || '暂无描述' }}</p>
        </div>

        <el-table :data="studentList" border stripe v-loading="studentListLoading">
          <el-table-column prop="studentNumber" label="学号" width="120"></el-table-column>
          <el-table-column prop="studentName" label="姓名" width="100"></el-table-column>
          <el-table-column prop="className" label="班级" width="120"></el-table-column>
          <el-table-column prop="uploadTime" label="上传时间" width="150">
            <template slot-scope="scope">
              {{ formatTime(scope.row.uploadTime) }}
            </template>
          </el-table-column>
          <el-table-column prop="gradingStatus" label="批阅状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.gradingStatus === 'completed' ? 'success' : 'warning'" size="small">
                {{ scope.row.gradingStatus === 'completed' ? '已批阅' : '未批阅' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="finalScore" label="得分" width="80">
            <template slot-scope="scope">
              {{ scope.row.finalScore || '-' }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="120" fixed="right">
            <template slot-scope="scope">
              <el-button
                type="primary"
                size="mini"
                @click="startGradingStudent(scope.row)"
              >
                {{ scope.row.gradingStatus === 'completed' ? '重新批阅' : '开始批阅' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-dialog>

    <!-- 阅卷对话框 -->
    <el-dialog
      title="阅卷"
      :visible.sync="gradingDialogVisible"
      width="95%"
      :before-close="closeGradingDialog"
      class="grading-dialog"
    >
      <div class="grading-container">
        <!-- 学生信息头部 -->
        <div class="student-header">
          <div class="student-info">
            <h3>{{ currentStudent.studentName }} ({{ currentStudent.studentNumber }})</h3>
            <p>{{ currentStudent.className }} - {{ currentStudent.paperCode }}卷</p>
          </div>
          <div class="grading-actions">
            <el-button type="primary" @click="saveGrading">保存批阅</el-button>
            <el-button type="success" @click="nextStudent">下一个学生</el-button>
            <el-button type="info" @click="prevStudent">上一个学生</el-button>
            <el-button @click="gradingDialogVisible = false">关闭</el-button>
          </div>
        </div>

        <div class="grading-content">
          <!-- 左侧：试卷图片 -->
          <div class="paper-viewer">
            <div class="viewer-header">
              <h4>试卷图片</h4>
             <!-- 批阅工具 -->
             <div class="viewer-tools">
                <!-- 批阅工具 -->
                <el-button-group>
                  <el-button size="mini" @click="toggleAnnotationMode" :type="annotationMode ? 'primary' : ''">
                    {{ annotationMode ? '退出标注' : '开始标注' }}
                  </el-button>
                  <el-button size="mini" @click="clearAnnotations">清除标注</el-button>
                </el-button-group>
                <!-- 缩放工具 -->
                <el-button-group style="margin-left: 10px;">
                  <el-button size="mini" @click="zoomIn">放大</el-button>
                  <el-button size="mini" @click="zoomOut">缩小</el-button>
                  <el-button size="mini" @click="resetZoom">重置</el-button>
                </el-button-group>
              </div>
            </div>
             <div class="grading-tools-horizontal">
               <div class="tool-section">
                 <label>标注类型：</label>
                 <el-radio-group v-model="currentAnnotationType" size="mini">
                   <el-radio-button label="correct">✓</el-radio-button>
                   <el-radio-button label="partial">△</el-radio-button>
                   <el-radio-button label="wrong">✗</el-radio-button>
                   <el-radio-button label="text">文字</el-radio-button>
                 </el-radio-group>
               </div>
               <div class="tool-section">
                 <label>标注颜色：</label>
                 <el-color-picker v-model="currentAnnotationColor" size="mini"></el-color-picker>
               </div>
               <div class="tool-section">
                 <label>标注大小：</label>
                 <el-slider v-model="currentAnnotationSize" :min="12" :max="48" :step="2" style="width: 100px;"></el-slider>
               </div>
               <div class="tool-section" v-if="currentAnnotationType === 'text'">
                 <label>标注文字：</label>
                 <el-input v-model="currentAnnotationText" placeholder="请输入标注文字" size="mini" style="width: 120px;"></el-input>
               </div>
             </div>

            <div class="image-container" ref="imageContainer">
              <!-- 图片切换控制 -->
              <div class="image-controls" v-if="processedPaperImages.length > 1">
                <el-button-group>
                  <el-button size="mini" @click="prevImage" :disabled="currentImageIndex === 0">
                    <i class="el-icon-arrow-left"></i> 上一张
                  </el-button>
                  <el-button size="mini" disabled>
                    {{ currentImageIndex + 1 }} / {{ processedPaperImages.length }}
                    <span v-if="getImageAnnotationCount(currentImageIndex) > 0" class="annotation-count">
                      ({{ getImageAnnotationCount(currentImageIndex) }}个标注)
                    </span>
                  </el-button>
                  <el-button size="mini" @click="nextImage" :disabled="currentImageIndex === processedPaperImages.length - 1">
                    下一张 <i class="el-icon-arrow-right"></i>
                  </el-button>
                </el-button-group>
              </div>

              <div class="image-wrapper" :style="{ transform: `scale(${zoomLevel})` }">
                <img
                  :src="currentImageUrl"
                  alt="试卷图片"
                  ref="paperImage"
                  @click="handleImageClick"
                  @mousemove="handleMouseMove"
                  @mouseleave="handleMouseLeave"
                  @error="handleImageError"
                />

                <!-- 标注层 -->
                <div class="annotation-layer">
                  <div
                    v-for="annotation in annotations"
                    :key="annotation.id"
                    class="annotation"
                    :style="{
                      left: annotation.x + 'px',
                      top: annotation.y + 'px',
                      color: annotation.color,
                      fontSize: annotation.size + 'px'
                    }"
                    @click="editAnnotation(annotation)"
                  >
                    {{ getAnnotationSymbol(annotation.type) }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧：题目列表和AI分析 -->
          <div class="grading-panel" :class="{ 'collapsed': panelCollapsed }">
            <!-- 面板控制按钮 -->
            <div class="panel-control">
              <el-button
                size="mini"
                :icon="panelCollapsed ? 'el-icon-arrow-left' : 'el-icon-arrow-right'"
                @click="togglePanel"
                circle
              ></el-button>
            </div>

            <!-- 题目列表 -->
            <div class="question-list" v-show="!panelCollapsed">
              <h4>题目列表</h4>
              <el-table :data="questionList" border size="mini">
                <el-table-column prop="questionOrder" label="题号" width="60" align="center"></el-table-column>
                <el-table-column prop="questionType" label="题型" width="80">
                  <template slot-scope="scope">
                    {{ getQuestionTypeText(scope.row.questionType) }}
                  </template>
                </el-table-column>
                <el-table-column prop="questionScore" label="分值" width="60" align="center"></el-table-column>
                <el-table-column label="得分" width="100">
                  <template slot-scope="scope">
                    <el-input-number
                      v-model="scope.row.finalScore"
                      :min="0"
                      :max="scope.row.questionScore"
                      size="mini"
                      @change="updateQuestionScore(scope.row)"
                    ></el-input-number>
                  </template>
                </el-table-column>
                <el-table-column label="状态" width="80">
                  <template slot-scope="scope">
                    <el-tag :type="getScoreStatusType(scope.row.finalScore, scope.row.questionScore)" size="mini">
                      {{ getScoreStatusText(scope.row.finalScore, scope.row.questionScore) }}
                    </el-tag>
                  </template>
                </el-table-column>
              </el-table>
            </div>

            <!-- AI分析结果 -->
            <div class="ai-analysis" v-if="aiAnalysis && !panelCollapsed">
              <h4>AI分析结果</h4>
              <div class="analysis-content">
                <div class="analysis-item" v-for="(analysis, index) in aiAnalysis" :key="index">
                  <div class="analysis-header">
                    <span class="question-number">第{{ analysis.questionOrder }}题</span>
                    <span class="ai-score">AI得分: {{ analysis.autoScore }}/{{ analysis.questionScore }}</span>
                  </div>
                  <div class="analysis-detail">
                    <p><strong>学生答案：</strong>{{ analysis.studentAnswer }}</p>
                    <p><strong>标准答案：</strong>{{ analysis.correctAnswer }}</p>
                    <p><strong>分析结果：</strong>{{ analysis.analysis }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 总分统计 -->
            <div class="score-summary" v-show="!panelCollapsed">
              <h4>得分统计</h4>
              <div class="summary-item">
                <span>总分：</span>
                <span class="total-score">{{ getTotalScore() }}/{{ getMaxScore() }}</span>
              </div>
              <div class="summary-item">
                <span>得分率：</span>
                <span class="score-rate">{{ getScoreRate() }}%</span>
              </div>
              <div class="summary-item">
                <span>批阅状态：</span>
                <el-tag :type="isGradingComplete() ? 'success' : 'warning'" size="mini">
                  {{ isGradingComplete() ? '已完成' : '进行中' }}
                </el-tag>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="gradingDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="saveGrading">保存批阅</el-button>
        <el-button type="success" @click="completeGrading">完成批阅</el-button>
      </div>
    </el-dialog>

    <!-- 标注编辑对话框 -->
    <el-dialog
      title="编辑标注"
      :visible.sync="annotationEditDialogVisible"
      width="400px"
    >
      <el-form :model="editingAnnotation" label-width="80px">
        <el-form-item label="标注类型">
          <el-select v-model="editingAnnotation.type" placeholder="请选择标注类型">
            <el-option label="正确" value="correct"></el-option>
            <el-option label="半对" value="partial"></el-option>
            <el-option label="错误" value="wrong"></el-option>
            <el-option label="文字" value="text"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="标注内容" v-if="editingAnnotation.type === 'text'">
          <el-input v-model="editingAnnotation.content" placeholder="请输入标注内容"></el-input>
        </el-form-item>
        <el-form-item label="标注颜色">
          <el-color-picker v-model="editingAnnotation.color"></el-color-picker>
        </el-form-item>
        <el-form-item label="标注大小">
          <el-slider v-model="editingAnnotation.size" :min="12" :max="48" :step="2"></el-slider>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button @click="annotationEditDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="updateAnnotation">确定</el-button>
        <el-button type="danger" @click="deleteAnnotation">删除</el-button>
      </div>
    </el-dialog>

    <!-- 归档设置对话框 -->
    <el-dialog
      title="归档设置"
      :visible.sync="archiveSettingsDialogVisible"
      width="500px"
      :before-close="closeArchiveSettingsDialog"
    >
      <div class="archive-settings">
        <p class="settings-tip">请设置归档后学生可以访问的内容：</p>
        <el-form ref="archiveSettingsForm" :model="archiveSettings" label-width="150px">
          <el-form-item label="发布学生成绩">
            <el-switch v-model="archiveSettings.allowViewScore"></el-switch>
            <span class="form-item-tip">学生可以查看自己的考试成绩</span>
          </el-form-item>
          <el-form-item label="允许学生查看试卷">
            <el-switch v-model="archiveSettings.allowViewPaper"></el-switch>
            <span class="form-item-tip">学生可以查看试卷和批注</span>
          </el-form-item>
          <el-form-item label="允许学生记录错题">
            <el-switch v-model="archiveSettings.allowRecordMistake"></el-switch>
            <span class="form-item-tip">学生可以将错题添加到错题本</span>
          </el-form-item>
          <el-form-item label="允许学生查看答题详情">
            <el-switch v-model="archiveSettings.allowViewAnswerDetail"></el-switch>
            <span class="form-item-tip">学生可以查看详细的答题情况</span>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="closeArchiveSettingsDialog">取消</el-button>
        <el-button type="primary" @click="confirmArchiveWithSettings">
          {{ currentArchiveExam && currentArchiveExam.status === 5 ? '保存设置' : '确定归档' }}
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { examApi } from '@/api/exam'

export default {
  name: 'ExamGrading',
  props: {
    courseId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      exams: [],
      selectedExams: [],
      showArchived: false,
      currentExam: {},
      currentStudent: {},
      studentList: [],
      studentListLoading: false,
      studentListDialogVisible: false,
      questionList: [],
      aiAnalysis: null,
      gradingDialogVisible: false,
      annotationEditDialogVisible: false,
      editingAnnotation: {},
      annotations: [],
      annotationMode: false,
      currentAnnotationType: 'correct',
      currentAnnotationColor: '#FF0000',
      currentAnnotationSize: 20,
      currentAnnotationText: '',
      zoomLevel: 1,
      mousePosition: { x: 0, y: 0 },
      // 多张图片支持
      processedPaperImages: [],
      currentImageIndex: 0,
      currentImageUrl: '',
      // 每张图片的标注信息
      imageAnnotations: {}, // key: imageIndex, value: annotations array
      // 归档设置对话框
      archiveSettingsDialogVisible: false,
      archiveSettings: {
        allowViewScore: true,
        allowViewPaper: true,
        allowRecordMistake: true,
        allowViewAnswerDetail: true
      },
      currentArchiveExam: null,
      // 面板折叠状态
      panelCollapsed: false,
      // 临时开关：禁用试卷图片预览，避免触发 /api/file/preview 请求
      disablePaperPreview: true
    }
  },
  mounted() {
    // 不在mounted时自动加载数据，等待父组件调用
    console.log('ExamGrading组件已挂载，courseId:', this.courseId)
  },
  methods: {
    // 加载未批阅的考试列表
    async loadUngradedExams() {
      console.log('开始加载未批阅考试列表，courseId:', this.courseId, 'showArchived:', this.showArchived)
      this.loading = true
      try {
        let response
        if (this.showArchived) {
          // 显示所有考试（包括已归档）
          console.log('调用getUnarchivedExamsWithActivePaper API')
          response = await examApi.getUnarchivedExamsWithActivePaper(this.courseId)
        } else {
          // 只显示未归档的考试
          console.log('调用getUngradedExams API')
          response = await examApi.getUngradedExams(this.courseId)
        }
        console.log('API响应:', response)
        this.exams = response.data || []
        console.log('考试列表:', this.exams)

        // 如果没有考试，不显示错误信息，空状态组件会显示"请先上传试卷"
        if (!this.exams || this.exams.length === 0) {
          // 不显示任何消息，让空状态组件处理
        }
      } catch (error) {
        // 检查是否是404错误（没有考试）还是其他错误
        if (error.response && error.response.status === 404) {
          this.exams = []
          // 不显示错误信息，让空状态组件显示"请先上传试卷"
        } else {
          this.$message.error('加载考试列表失败')
        }
      } finally {
        this.loading = false
      }
    },

    // 加载数据（供父组件调用）
    async loadData() {
      console.log('ExamGrading loadData被调用，courseId:', this.courseId)
      await this.loadUngradedExams()
    },

    // 处理选择变化
    handleSelectionChange(selection) {
      this.selectedExams = selection
    },

    // 打开考试阅卷
    openExamGrading(exam) {
      this.currentExam = exam
      this.gradingDialogVisible = true
      this.loadFirstUngradedStudent()
    },

    // 切换面板折叠状态
    togglePanel() {
      this.panelCollapsed = !this.panelCollapsed
    },

    // 开始阅卷
    async startGrading(exam) {
      this.currentExam = exam
      this.studentListDialogVisible = true
      await this.loadStudentList()
    },

    // 加载学生列表
    async loadStudentList() {
      this.studentListLoading = true
      try {
        const response = await examApi.getExamStudents(this.currentExam.id)
        this.studentList = response.data || []
      } catch (error) {
        this.$message.error('加载学生列表失败')
        this.studentList = []
      } finally {
        this.studentListLoading = false
      }
    },

    // 开始批阅学生
    async startGradingStudent(student) {
      console.log('开始批阅学生:', student)
      console.log('学生批阅状态:', student.gradingStatus)
      console.log('学生最终得分:', student.finalScore)

      this.currentStudent = student
      if (!this.disablePaperPreview) {
        this.processPaperImages(student.paperImageUrl)
      }
      this.studentListDialogVisible = false
      this.gradingDialogVisible = true
      await this.loadStudentGradingData()
    },

    // 关闭学生列表对话框
    closeStudentListDialog() {
      this.studentListDialogVisible = false
      this.currentExam = {}
      this.studentList = []
    },

    // 归档考试
    async archiveExam(exam) {
      try {
        // 保存当前要归档的考试
        this.currentArchiveExam = exam

        // 重置归档设置
        this.archiveSettings = {
          allowViewScore: true,
          allowViewPaper: true,
          allowRecordMistake: true,
          allowViewAnswerDetail: true
        }

        // 显示归档设置对话框
        this.archiveSettingsDialogVisible = true
      } catch (error) {
        this.$message.error('打开归档设置失败')
      }
    },

    // 确认归档并应用设置
    async confirmArchiveWithSettings() {
      try {
        if (!this.currentArchiveExam) {
          this.$message.error('无法获取考试信息')
          return
        }

        const isArchived = this.currentArchiveExam.status === 5

        // 如果还未归档，先调用归档API
        if (!isArchived) {
          await examApi.archiveExam(this.currentArchiveExam.id)
        }

        // 调用更新归档设置API
        await examApi.updateArchiveSettings(this.currentArchiveExam.id, {
          allowViewScore: this.archiveSettings.allowViewScore ? 1 : 0,
          allowViewPaper: this.archiveSettings.allowViewPaper ? 1 : 0,
          allowRecordMistake: this.archiveSettings.allowRecordMistake ? 1 : 0,
          allowViewAnswerDetail: this.archiveSettings.allowViewAnswerDetail ? 1 : 0
        })

        this.$message.success(isArchived ? '归档设置更新成功' : '考试归档成功')

        // 关闭对话框
        this.archiveSettingsDialogVisible = false
        this.currentArchiveExam = null

        // 重新加载考试列表
        await this.loadUngradedExams()
      } catch (error) {
        this.$message.error('操作失败')
      }
    },

    // 关闭归档设置对话框
    closeArchiveSettingsDialog() {
      this.archiveSettingsDialogVisible = false
      this.currentArchiveExam = null
    },

    // 编辑已归档考试的设置
    async editArchiveSettings(exam) {
      try {
        // 保存当前要编辑的考试
        this.currentArchiveExam = exam

        // 如果有归档设置数据，使用现有数据；否则使用默认值
        if (exam.allowViewScore !== undefined && exam.allowViewPaper !== undefined && exam.allowRecordMistake !== undefined) {
          this.archiveSettings = {
            allowViewScore: exam.allowViewScore === 1,
            allowViewPaper: exam.allowViewPaper === 1,
            allowRecordMistake: exam.allowRecordMistake === 1,
            allowViewAnswerDetail: exam.allowViewAnswerDetail === 1
          }
        } else {
          // 使用默认值
          this.archiveSettings = {
            allowViewScore: true,
            allowViewPaper: true,
            allowRecordMistake: true,
            allowViewAnswerDetail: true
          }
        }

        // 显示归档设置对话框
        this.archiveSettingsDialogVisible = true
      } catch (error) {
        this.$message.error('打开归档设置失败')
      }
    },

    // 取消归档考试
    async unarchiveExam(exam) {
      try {
        await this.$confirm(
          `确定要取消归档考试"${exam.examName}"吗？取消归档后该考试将重新显示在阅卷列表中。`,
          '确认取消归档',
          {
            confirmButtonText: '确定取消归档',
            cancelButtonText: '取消',
            type: 'info'
          }
        )

        // 调用取消归档API
        await examApi.unarchiveExam(exam.id)

        this.$message.success('取消归档成功')

        // 重新加载考试列表
        await this.loadUngradedExams()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('取消归档失败')
        }
      }
    },

    // 批量归档
    async batchArchive() {
      if (this.selectedExams.length === 0) {
        this.$message.warning('请选择要归档的考试')
        return
      }

      try {
        await this.$confirm(
          `确定要归档选中的 ${this.selectedExams.length} 个考试吗？`,
          '批量归档确认',
          {
            confirmButtonText: '确定归档',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        // 批量归档
        const archivePromises = this.selectedExams.map(exam => examApi.archiveExam(exam.id))
        await Promise.all(archivePromises)

        this.$message.success(`成功归档 ${this.selectedExams.length} 个考试`)

        // 清空选择并重新加载
        this.selectedExams = []
        await this.loadUngradedExams()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('批量归档失败')
        }
      }
    },

    // 加载第一个未批阅的学生
    async loadFirstUngradedStudent() {
      try {
        const response = await examApi.getFirstUngradedStudent(this.currentExam.id)
        if (response.data) {
          this.currentStudent = response.data
          await this.loadStudentGradingData()
        } else {
          this.$message.info('该考试所有学生都已批阅完成')
          this.gradingDialogVisible = false
        }
      } catch (error) {
        this.$message.error('加载学生数据失败')
      }
    },

    // 加载学生批阅数据
    async loadStudentGradingData() {
      try {
        // 加载题目列表 - 从API获取真实数据
        await this.loadStudentQuestions()

        // 加载AI分析结果 - 模拟数据
        this.aiAnalysis = this.generateMockAIAnalysis()

        // 加载已有标注
        await this.loadAnnotations()

        // 重置标注模式
        this.annotationMode = false
        this.zoomLevel = 1
      } catch (error) {
        this.$message.error('加载批阅数据失败')
      }
    },

    // 加载学生题目列表
    async loadStudentQuestions() {
      try {
        const response = await examApi.getStudentQuestions(this.currentExam.id, this.currentStudent.studentId)
        console.log('题目列表API响应:', response)
        console.log('题目列表数据:', response.data)

        if (response.data && response.data.length > 0) {
          // 使用API返回的真实数据
          this.questionList = response.data.map(q => {
            console.log('处理题目数据:', q)
            return {
              questionId: parseInt(q.questionId),
              questionOrder: parseInt(q.questionOrder),
              questionType: parseInt(q.questionType),
              questionScore: parseInt(q.questionScore),
              finalScore: parseInt(q.finalScore) || 0 // 确保转换为整数
            }
          })
          console.log('处理后的题目列表:', this.questionList)
        } else {
          // 如果API没有返回数据，使用模拟数据作为备选
          console.log('API没有返回数据，使用模拟数据')
          this.questionList = this.generateMockQuestions()
        }
      } catch (error) {
        console.error('加载学生题目列表失败:', error)
        // 如果API调用失败，使用模拟数据作为备选
        this.questionList = this.generateMockQuestions()
      }
    },

    // 加载标注信息
    async loadAnnotations() {
      try {
        // 检查必要的参数
        if (!this.currentExam || !this.currentExam.id) {
          console.warn('考试ID不存在，跳过加载标注')
          this.annotations = []
          this.imageAnnotations = {}
          return
        }

        if (!this.currentStudent || !this.currentStudent.studentId) {
          console.warn('学生ID不存在，跳过加载标注')
          this.annotations = []
          this.imageAnnotations = {}
          return
        }

        const response = await examApi.getAnnotations(this.currentExam.id, this.currentStudent.studentId)
        const allAnnotations = response.data || []

        console.log('从后端加载到标注信息:', allAnnotations)

        // 按图片索引分组标注信息
        this.imageAnnotations = {}
        for (const annotation of allAnnotations) {
          const imageIndex = annotation.imageIndex
          if (imageIndex !== null && imageIndex !== undefined) {
            // 移除imageIndex字段，避免重复存储
            const { imageIndex: _, ...annotationData } = annotation
            if (!this.imageAnnotations[imageIndex]) {
              this.imageAnnotations[imageIndex] = []
            }
            this.imageAnnotations[imageIndex].push(annotationData)
          }
        }

        // 加载当前图片的标注信息
        this.loadImageAnnotations(this.currentImageIndex)

        console.log('标注信息分组完成:', this.imageAnnotations)
        console.log('当前图片标注数量:', this.annotations.length)
      } catch (error) {
        console.error('加载标注信息失败:', error)
        this.annotations = []
        this.imageAnnotations = {}
      }
    },

    // 生成模拟题目数据
    generateMockQuestions() {
      return [
        { questionId: 1, questionOrder: 1, questionType: 1, questionScore: 5, finalScore: 0 },
        { questionId: 2, questionOrder: 2, questionType: 1, questionScore: 5, finalScore: 0 },
        { questionId: 3, questionOrder: 3, questionType: 2, questionScore: 10, finalScore: 0 },
        { questionId: 4, questionOrder: 4, questionType: 2, questionScore: 10, finalScore: 0 },
        { questionId: 5, questionOrder: 5, questionType: 3, questionScore: 5, finalScore: 0 },
        { questionId: 6, questionOrder: 6, questionType: 4, questionScore: 15, finalScore: 0 },
        { questionId: 7, questionOrder: 7, questionType: 4, questionScore: 20, finalScore: 0 },
        { questionId: 8, questionOrder: 8, questionType: 4, questionScore: 30, finalScore: 0 }
      ]
    },

    // 生成模拟AI分析数据
    generateMockAIAnalysis() {
      return [
        {
          questionOrder: 1,
          questionScore: 5,
          autoScore: 5,
          studentAnswer: "A",
          correctAnswer: "A",
          analysis: "答案正确，学生选择了正确答案A。"
        },
        {
          questionOrder: 2,
          questionScore: 5,
          autoScore: 0,
          studentAnswer: "B",
          correctAnswer: "A",
          analysis: "答案错误，学生选择了B，正确答案是A。"
        },
        {
          questionOrder: 3,
          questionScore: 10,
          autoScore: 8,
          studentAnswer: "北京",
          correctAnswer: "北京",
          analysis: "答案基本正确，但字迹不够清晰，扣2分。"
        }
      ]
    },

    // 下一个学生
    async nextStudent() {
      try {
        const response = await examApi.getNextUngradedStudent(this.currentExam.id, this.currentStudent.studentId)
        if (response.data) {
          this.currentStudent = response.data
          await this.loadStudentGradingData()
        } else {
          this.$message.info('没有更多未批阅的学生')
          // 不要关闭对话框，让用户可以继续当前学生的批阅
        }
      } catch (error) {
        console.error('加载下一个学生失败:', error)
        this.$message.error('加载下一个学生失败')
      }
    },

    // 上一个学生
    async prevStudent() {
      try {
        const response = await examApi.getPrevStudent(this.currentExam.id, this.currentStudent.studentId)
        if (response.data) {
          this.currentStudent = response.data
          await this.loadStudentGradingData()
        } else {
          this.$message.info('没有上一个学生')
        }
      } catch (error) {
        this.$message.error('加载上一个学生失败')
      }
    },

    // 保存批阅
    async saveGrading() {
      try {
        // 先保存当前图片的标注信息
        this.saveCurrentImageAnnotations()

        // 收集所有图片的标注信息
        const allAnnotations = []
        for (let i = 0; i < this.processedPaperImages.length; i++) {
          if (this.imageAnnotations[i] && this.imageAnnotations[i].length > 0) {
            // 为每个标注添加图片索引信息
            const imageAnnotations = this.imageAnnotations[i].map(annotation => ({
              ...annotation,
              imageIndex: i // 添加图片索引
            }))
            allAnnotations.push(...imageAnnotations)
          }
        }

        console.log('保存所有图片的标注信息:', allAnnotations)
        console.log('标注分布:', Object.keys(this.imageAnnotations).map(key =>
          `图片${parseInt(key) + 1}: ${this.imageAnnotations[key].length}个标注`
        ))

        const data = {
          examId: this.currentExam.id,
          studentId: this.currentStudent.studentId,
          questionScores: this.questionList.map(q => ({
            questionId: q.questionId,
            finalScore: q.finalScore
          })),
          annotations: allAnnotations
        }
        await examApi.saveGrading(data)
        this.$message.success(`保存批阅成功，共保存${allAnnotations.length}个标注`)
      } catch (error) {
        console.error('保存批阅失败:', error)
        this.$message.error('保存批阅失败')
      }
    },

    // 完成批阅
    async completeGrading() {
      try {
        await this.saveGrading()
        await examApi.completeGrading(this.currentExam.id, this.currentStudent.studentId)
        this.$message.success('完成批阅')
        await this.nextStudent()
      } catch (error) {
        this.$message.error('完成批阅失败')
      }
    },

    // 更新题目得分
    async updateQuestionScore(question) {
      try {
        console.log('更新题目得分:', {
          examId: this.currentExam.id,
          studentId: this.currentStudent.studentId,
          questionId: question.questionId,
          finalScore: question.finalScore
        })

        await examApi.updateQuestionScore(this.currentExam.id, this.currentStudent.studentId, question.questionId, question.finalScore)
        // 更新成功后重新加载题目列表以获取最新数据
        await this.loadStudentQuestions()
        console.log(`题目${question.questionId}得分已更新为${question.finalScore}`)
      } catch (error) {
        console.error('更新得分失败:', error)
        this.$message.error('更新得分失败')
      }
    },

    // 图片点击处理
    handleImageClick(event) {
      if (!this.annotationMode) return

      const rect = this.$refs.paperImage.getBoundingClientRect()
      const x = (event.clientX - rect.left) / this.zoomLevel
      const y = (event.clientY - rect.top) / this.zoomLevel

      const annotation = {
        id: Date.now(),
        type: this.currentAnnotationType,
        content: this.currentAnnotationType === 'text' ? this.currentAnnotationText : '',
        color: this.currentAnnotationColor,
        size: this.currentAnnotationSize,
        x: x,
        y: y
      }

      this.annotations.push(annotation)

      // 自动保存当前图片的标注信息
      this.saveCurrentImageAnnotations()
    },

    // 鼠标移动处理
    handleMouseMove(event) {
      const rect = this.$refs.paperImage.getBoundingClientRect()
      this.mousePosition = {
        x: (event.clientX - rect.left) / this.zoomLevel,
        y: (event.clientY - rect.top) / this.zoomLevel
      }
    },

    // 鼠标离开处理
    handleMouseLeave() {
      this.mousePosition = { x: 0, y: 0 }
    },

    // 切换标注模式
    toggleAnnotationMode() {
      this.annotationMode = !this.annotationMode
    },

    // 清除当前图片的标注
    clearAnnotations() {
      this.$confirm('确定要清除当前图片的所有标注吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.annotations = []
        // 同时清除存储的标注信息
        this.imageAnnotations[this.currentImageIndex] = []
        console.log(`清除第${this.currentImageIndex + 1}张图片的标注信息`)
      })
    },

    // 编辑标注
    editAnnotation(annotation) {
      this.editingAnnotation = { ...annotation }
      this.annotationEditDialogVisible = true
    },

    // 更新标注
    updateAnnotation() {
      const index = this.annotations.findIndex(a => a.id === this.editingAnnotation.id)
      if (index !== -1) {
        this.annotations.splice(index, 1, { ...this.editingAnnotation })
      }
      this.annotationEditDialogVisible = false
    },

    // 删除标注
    deleteAnnotation() {
      const index = this.annotations.findIndex(a => a.id === this.editingAnnotation.id)
      if (index !== -1) {
        this.annotations.splice(index, 1)
      }
      this.annotationEditDialogVisible = false
    },

    // 放大
    zoomIn() {
      this.zoomLevel = Math.min(this.zoomLevel + 0.2, 3)
    },

    // 缩小
    zoomOut() {
      this.zoomLevel = Math.max(this.zoomLevel - 0.2, 0.5)
    },

    // 重置缩放
    resetZoom() {
      this.zoomLevel = 1
    },

    // 获取标注符号
    getAnnotationSymbol(type) {
      const symbols = {
        correct: '✓',
        partial: '△',
        wrong: '✗',
        text: this.editingAnnotation.content || '文字'
      }
      return symbols[type] || '?'
    },

    // 获取题目类型文本
    getQuestionTypeText(type) {
      const typeMap = {
        1: '选择题',
        2: '填空题',
        3: '判断题',
        4: '简答题'
      }
      return typeMap[type] || '未知'
    },

    // 获取得分状态类型
    getScoreStatusType(score, maxScore) {
      const scoreNum = parseInt(score) || 0
      const maxScoreNum = parseInt(maxScore) || 0
      if (scoreNum === maxScoreNum) return 'success'
      if (scoreNum > 0) return 'warning'
      return 'danger'
    },

    // 获取得分状态文本
    getScoreStatusText(score, maxScore) {
      const scoreNum = parseInt(score) || 0
      const maxScoreNum = parseInt(maxScore) || 0
      if (scoreNum === maxScoreNum) return '满分'
      if (scoreNum > 0) return '部分'
      return '零分'
    },

    // 获取总分
    getTotalScore() {
      return this.questionList.reduce((sum, q) => sum + (parseInt(q.finalScore) || 0), 0)
    },

    // 获取满分
    getMaxScore() {
      return this.questionList.reduce((sum, q) => sum + (parseInt(q.questionScore) || 0), 0)
    },

    // 获取得分率
    getScoreRate() {
      const total = this.getTotalScore()
      const max = this.getMaxScore()
      return max > 0 ? Math.round((total / max) * 100) : 0
    },

    // 是否批阅完成
    isGradingComplete() {
      return this.questionList.every(q => {
        const finalScore = parseInt(q.finalScore)
        return finalScore !== null && finalScore !== undefined && !isNaN(finalScore)
      })
    },

    // 获取批阅进度
    getGradingProgress(exam) {
      if (exam.totalStudents === 0) return 0
      return Math.round((exam.gradedStudents / exam.totalStudents) * 100)
    },

    // 关闭阅卷对话框
    closeGradingDialog() {
      this.gradingDialogVisible = false
      this.currentExam = {}
      this.currentStudent = {}
      this.questionList = []
      this.aiAnalysis = null
      this.annotations = []
      this.annotationMode = false
      this.zoomLevel = 1
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        1: '草稿',
        2: '已发布',
        3: '进行中',
        4: '已结束',
        5: '已归档'
      }
      return statusMap[status] || '未知'
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        1: 'info', // 草稿 - 灰色
        2: 'success', // 已发布 - 绿色
        3: 'warning', // 进行中 - 橙色
        4: 'danger', // 已结束 - 红色
        5: 'info' // 已归档 - 灰色
      }
      return typeMap[status] || 'info'
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleString()
    },

    // 处理试卷图片URL，支持多张图片
    processPaperImages(imageUrl) {
      this.processedPaperImages = []

      if (!imageUrl) {
        return
      }

      // 检查是否是JSON数组格式（多张图片）
      if (imageUrl.startsWith('[') && imageUrl.endsWith(']')) {
        try {
          const imageArray = JSON.parse(imageUrl)
          this.processedPaperImages = imageArray.map(url => this.getFullImageUrl(url))
        } catch (e) {
          console.error('解析图片URL数组失败:', e)
          this.processedPaperImages = [this.getFullImageUrl(imageUrl)]
        }
      } else if (imageUrl.includes(',')) {
        // 逗号分隔的多个URL
        const urls = imageUrl.split(',').map(url => url.trim())
        this.processedPaperImages = urls.map(url => this.getFullImageUrl(url))
      } else {
        // 单张图片
        this.processedPaperImages = [this.getFullImageUrl(imageUrl)]
      }

      // 设置当前图片
      this.currentImageIndex = 0
      this.currentImageUrl = this.processedPaperImages[0] || ''

      // 初始化标注信息存储
      this.imageAnnotations = {}
      this.annotations = []

      console.log(`初始化${this.processedPaperImages.length}张图片的标注存储`)
    },

    // 图像加载错误处理
    handleImageError(event) {
      console.error('教师端 - 图像加载失败:', event.target.src)
      console.error('教师端 - 原始URL:', this.currentImageUrl)
      this.$message.error(`图像加载失败: ${this.currentImageUrl}`)
    },

    // 获取完整的图片URL
    getFullImageUrl(imageUrl) {
      if (!imageUrl) return ''

      // 如果已经是完整URL，直接返回
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl
      }

      // 如果是API预览接口，使用前端API基础URL
      if (imageUrl.startsWith('/api/file/preview')) {
        // 移除开头的 /api，因为 VUE_APP_BASE_API 已经包含了 /api
        const pathWithoutApi = imageUrl.substring(4) // 移除 '/api'
        return process.env.VUE_APP_BASE_API + pathWithoutApi
      }

      // 如果是MinIO路径，使用MinIO端口
      if (imageUrl.startsWith('/exam-papers/')) {
        return `http://localhost:9000${imageUrl}`
      }

      // 其他情况使用API端口
      return `http://localhost:8080${imageUrl}`
    },

    // 获取指定图片的标注数量
    getImageAnnotationCount(imageIndex) {
      return this.imageAnnotations[imageIndex] ? this.imageAnnotations[imageIndex].length : 0
    },

    // 保存当前图片的标注信息
    saveCurrentImageAnnotations() {
      if (this.annotations && this.annotations.length > 0) {
        this.imageAnnotations[this.currentImageIndex] = [...this.annotations]
        console.log(`保存第${this.currentImageIndex + 1}张图片的标注信息:`, this.annotations)
      }
    },

    // 加载指定图片的标注信息
    loadImageAnnotations(imageIndex) {
      if (this.imageAnnotations[imageIndex]) {
        this.annotations = [...this.imageAnnotations[imageIndex]]
        console.log(`加载第${imageIndex + 1}张图片的标注信息:`, this.annotations)
      } else {
        this.annotations = []
        console.log(`第${imageIndex + 1}张图片暂无标注信息`)
      }
    },

    // 上一张图片
    prevImage() {
      if (this.currentImageIndex > 0) {
        // 保存当前图片的标注信息
        this.saveCurrentImageAnnotations()

        // 切换到上一张图片
        this.currentImageIndex--
        this.currentImageUrl = this.processedPaperImages[this.currentImageIndex]

        // 加载新图片的标注信息
        this.loadImageAnnotations(this.currentImageIndex)
      }
    },

    // 下一张图片
    nextImage() {
      if (this.currentImageIndex < this.processedPaperImages.length - 1) {
        // 保存当前图片的标注信息
        this.saveCurrentImageAnnotations()

        // 切换到下一张图片
        this.currentImageIndex++
        this.currentImageUrl = this.processedPaperImages[this.currentImageIndex]

        // 加载新图片的标注信息
        this.loadImageAnnotations(this.currentImageIndex)
      }
    }
  }
}
</script>

<style scoped>
.exam-grading {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 20px;
  display: block;
}

.empty-tip {
  font-size: 14px;
  color: #C0C4CC;
  margin-top: 10px;
}

.exam-name {
  color: #409EFF;
  cursor: pointer;
  text-decoration: none;
}

.exam-name:hover {
  text-decoration: underline;
}

.exam-list {
  min-height: 400px;
}

.exam-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.exam-card:hover {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.exam-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.exam-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.exam-actions {
  display: flex;
  gap: 5px;
}

.exam-content {
  margin-bottom: 15px;
}

.exam-description {
  color: #606266;
  font-size: 14px;
  margin-bottom: 15px;
  line-height: 1.5;
}

.exam-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.meta-item {
  display: flex;
  align-items: center;
  color: #909399;
  font-size: 12px;
}

.meta-item i {
  margin-right: 5px;
}

.exam-footer {
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.progress-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.progress-text {
  font-size: 12px;
  color: #606266;
  min-width: 60px;
}

.progress-percentage {
  font-size: 12px;
  color: #409eff;
  min-width: 40px;
  text-align: right;
}

.grading-dialog .el-dialog__body {
  padding: 0;
}

.grading-container {
  height: 85vh;
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.student-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e4e7ed;
}

.student-info h3 {
  margin: 0 0 5px 0;
  color: #303133;
}

.student-info p {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

.grading-actions {
  display: flex;
  gap: 10px;
}

.grading-content {
  flex: 1;
  display: flex;
  overflow: auto;
  min-height: 0;
}

.paper-viewer {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e4e7ed;
}

.viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: #fafafa;
  border-bottom: 1px solid #e4e7ed;
}

.viewer-header h4 {
  margin: 0;
  color: #303133;
}

.viewer-tools {
  display: flex;
  gap: 10px;
}

.image-container {
  flex: 1;
  overflow: auto;
  padding: 20px;
  text-align: center;
}

.image-wrapper {
  position: relative;
  display: inline-block;
  transition: transform 0.3s;
}

.image-wrapper img {
  max-width: 100%;
  height: auto;
  cursor: crosshair;
}

.annotation-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.annotation {
  position: absolute;
  pointer-events: auto;
  cursor: pointer;
  font-weight: bold;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  transition: all 0.3s;
}

.annotation:hover {
  transform: scale(1.2);
}

.grading-panel {
  width: 500px;
  display: flex;
  flex-direction: column;
  background: #fafafa;
  position: relative;
  transition: width 0.3s ease;
  overflow-y: auto;
  max-height: 100%;
}

.grading-panel.collapsed {
  width: 50px;
}

.panel-control {
  position: absolute;
  top: 20px;
  right: 10px;
  z-index: 10;
}

.panel-control .el-button {
  background: #409eff;
  border-color: #409eff;
  color: white;
}

.panel-control .el-button:hover {
  background: #66b1ff;
  border-color: #66b1ff;
}

.question-list,
.ai-analysis,
.grading-tools,
.score-summary {
  padding: 20px;
  border-bottom: 1px solid #e4e7ed;
  flex-shrink: 0;
}

.question-list:last-child,
.ai-analysis:last-child,
.grading-tools:last-child,
.score-summary:last-child {
  border-bottom: none;
}

.question-list h4,
.ai-analysis h4,
.grading-tools h4,
.score-summary h4 {
  margin: 0 0 15px 0;
  color: #303133;
  font-size: 14px;
}

.analysis-content {
  max-height: 200px;
  overflow-y: auto;
}

.analysis-item {
  margin-bottom: 15px;
  padding: 10px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.question-number {
  font-weight: bold;
  color: #303133;
}

.ai-score {
  color: #409eff;
  font-size: 12px;
}

.analysis-detail p {
  margin: 5px 0;
  font-size: 12px;
  color: #606266;
}

.tool-section {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.tool-section label {
  display: inline-block;
  margin-bottom: 0;
  font-size: 12px;
  color: #606266;
  min-width: 60px;
  text-align: right;
}

.grading-tools-horizontal {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

.grading-tools-horizontal .tool-section {
  margin-bottom: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.grading-tools-horizontal .tool-section label {
  min-width: 50px;
  font-size: 12px;
  color: #606266;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  font-size: 14px;
}

.total-score {
  font-weight: bold;
  color: #409eff;
}

.score-rate {
  font-weight: bold;
  color: #67c23a;
}

.dialog-footer {
  text-align: right;
}

.student-list-container {
  padding: 20px;
}

.exam-info {
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
}

.exam-info h3 {
  margin: 0 0 10px 0;
  color: #303133;
}

.exam-info p {
  margin: 0;
  color: #606266;
  font-size: 14px;
}

/* 图片控制按钮样式 */
.image-controls {
  margin-bottom: 15px;
  text-align: center;
}

.annotation-count {
  color: #67c23a;
  font-size: 11px;
  margin-left: 5px;
}

.image-controls .el-button-group {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.image-controls .el-button {
  border-radius: 4px;
}

.image-controls .el-button:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.image-controls .el-button:last-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.image-controls .el-button:not(:first-child):not(:last-child) {
  border-radius: 0;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .grading-panel {
    width: 400px;
  }

  .grading-panel.collapsed {
    width: 40px;
  }
}

@media (max-width: 992px) {
  .grading-content {
    flex-direction: column;
  }

  .paper-viewer {
    border-right: none;
    border-bottom: 1px solid #e4e7ed;
    min-height: 400px;
  }

  .grading-panel {
    width: 100%;
    min-height: 300px;
  }

  .grading-panel.collapsed {
    width: 100%;
    min-height: 50px;
  }

  .student-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .grading-actions {
    width: 100%;
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  .grading-dialog {
    width: 95% !important;
    margin: 0 auto;
  }

  .viewer-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .viewer-tools {
    width: 100%;
    flex-wrap: wrap;
  }

  .grading-tools-horizontal {
    flex-direction: column;
    gap: 10px;
  }

  .tool-section {
    width: 100%;
  }

  .tool-section label {
    display: block;
    margin-bottom: 5px;
  }
}

/* 归档设置对话框样式 */
.archive-settings {
  padding: 20px 0;
}

.settings-tip {
  margin-bottom: 20px;
  color: #606266;
  font-size: 14px;
}

.archive-settings .el-form-item {
  margin-bottom: 20px;
}

.archive-settings .el-form-item:last-child {
  margin-bottom: 0;
}

.form-item-tip {
  margin-left: 10px;
  color: #909399;
  font-size: 12px;
}
</style>
